import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { first } from "rxjs/operators";
import { DialogRefreshComponent } from "../common/dialog-refresh/dialog-refresh.component";
import { AppUpdatesApiService } from "./app-updates-api.service";

// FLIP-78. Snooze is timestamp-based (an expiry stored in localStorage, compared on the
// next poll) rather than a live setTimeout/setInterval delay, because mobile throttles
// timers while the WebView is backgrounded - a stored expiry survives that regardless of
// whether anything was running to count it down.
const SNOOZE_STORAGE_KEY = "versionUpdateSnooze";
const REQUIRED_SNOOZE_MS = 1000 * 60 * 10; // fixed 10 minutes
const OPTIONAL_SNOOZE_MIN_MS = 1000 * 60 * 10;
const OPTIONAL_SNOOZE_MAX_MS = 1000 * 60 * 20; // 10-20 minutes, randomised

interface VersionUpdateSnooze {
  hash: string;
  until: number;
}

@Injectable()
export class VersionCheckService {
  // this will be replaced by actual hash post-build.js
  private currentHash = "{{POST_BUILD_ENTERS_HASH_HERE}}";

  constructor(
    private http: HttpClient,
    private matDialog: MatDialog,
    private appUpdatesApiService: AppUpdatesApiService
  ) {}

  /**
   * Checks in every set frequency the version of frontend application
   * @param url
   * @param {number} frequency - in milliseconds, defaults to 10 minutes
   */
  public initVersionCheck(url, frequency = 1000 * 60 * 10) {
    setInterval(() => {
      this.checkVersion(url);
    }, frequency);
  }

  public versionCheck(url) {
    // 60 * 60 * 6
    this.checkVersion(url);
  }

  /**
   * Will do the call and check if the hash has changed or not
   * @param url
   */
  private checkVersion(url) {
    // timestamp these requests to invalidate caches
    if (
      location.hostname === "localhost" ||
      location.hostname === "127.0.0.1"
    ) {
      return;
    }

    this.http
      .get(url + "?t=" + new Date().getTime())
      .pipe(first())
      .subscribe(
        (response: any) => {
          const hash = response.hash;
          const version = response.version;
          const hashChanged = this.hasHashChanged(this.currentHash, hash);

          if (hashChanged) {
            this.resolveIsRequiredAndMaybeShow(hash, version);
          }
          // Deliberately NOT reassigning this.currentHash here (as the pre-FLIP-78
          // version did): currentHash represents the hash baked into the bundle that's
          // actually running, and only changes via a real reload onto the new build. If
          // it were bumped to match the server's hash just from seeing it in a poll
          // response, hasHashChanged() would go false on every later poll and a snoozed
          // dialog could never re-show.
        },
        (err) => {
          console.error(err, "Could not get version");
        }
      );
  }

  /**
   * The optional/required decision comes from AppUpdateAnnouncements.IsRequired (FLIP-80),
   * via a small unauthenticated lookup - not from a field on version.json, which never
   * carried one: post-build.js only ever writes {version, hash}, so reading
   * `response.isRequired` off it (the original FLIP-78 stopgap) meant 1B could never
   * actually fire in production. Falls back to optional on a missing version or a failed
   * lookup - the same safe default the old code effectively always hit anyway.
   */
  private resolveIsRequiredAndMaybeShow(hash: string, version: string): void {
    if (!version) {
      this.maybeShowUpdateDialog(hash, false);
      return;
    }

    this.appUpdatesApiService.isRequired(version).subscribe({
      next: (response) => {
        this.maybeShowUpdateDialog(hash, !!response?.responseData);
      },
      error: (err) => {
        console.error(err, "Could not check whether the update is required");
        this.maybeShowUpdateDialog(hash, false);
      },
    });
  }

  /**
   * Opens the update dialog for the given hash unless one is already open, or this hash
   * is still within its snooze window. A required update whose snooze has already expired
   * re-shows blocking - Update now only, no dismiss.
   */
  private maybeShowUpdateDialog(hash: string, isRequired: boolean) {
    if (this.matDialog.openDialogs.length > 0) {
      return;
    }

    const snooze = this.readSnooze();
    const isSnoozedForThisHash = !!snooze && snooze.hash === hash;
    const snoozeExpired = isSnoozedForThisHash && Date.now() >= snooze.until;

    if (isSnoozedForThisHash && !snoozeExpired) {
      // Still within the snooze window - the next poll will check again.
      return;
    }

    const blocking = isRequired && isSnoozedForThisHash && snoozeExpired;

    const dialogRef = this.matDialog.open(DialogRefreshComponent, {
      panelClass: "base-modal-panel",
      backdropClass: "base-modal-backdrop",
      disableClose: true,
      data: { isRequired, blocking },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result === "snooze") {
        const duration = isRequired
          ? REQUIRED_SNOOZE_MS
          : this.randomOptionalSnoozeMs();
        this.writeSnooze(hash, Date.now() + duration);
      }
      // "update" reloads the page itself - nothing to snooze.
    });
  }

  private randomOptionalSnoozeMs(): number {
    return (
      OPTIONAL_SNOOZE_MIN_MS +
      Math.random() * (OPTIONAL_SNOOZE_MAX_MS - OPTIONAL_SNOOZE_MIN_MS)
    );
  }

  private readSnooze(): VersionUpdateSnooze | null {
    try {
      const raw = localStorage.getItem(SNOOZE_STORAGE_KEY);
      return raw ? (JSON.parse(raw) as VersionUpdateSnooze) : null;
    } catch {
      return null;
    }
  }

  private writeSnooze(hash: string, until: number): void {
    localStorage.setItem(SNOOZE_STORAGE_KEY, JSON.stringify({ hash, until }));
  }

  /**
   * Checks if hash has changed.
   * This file has the JS hash, if it is a different one than in the version.json
   * we are dealing with version change
   * @param currentHash
   * @param newHash
   * @returns {boolean}
   */
  private hasHashChanged(currentHash, newHash) {
    if (!currentHash || currentHash === "{{POST_BUILD_ENTERS_HASH_HERE}}") {
      return false;
    }

    return currentHash !== newHash;
  }
}
